import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

/**
 * Returns the user's subscription tier. Reads `subscription_tier_id` from
 * the centralized `useProfile` cache, then fetches the tier row (also cached).
 */
export function useUserTier() {
  const { profile, isLoading: profileLoading } = useProfile();
  const tierId = profile?.subscription_tier_id ?? null;

  const { data, isLoading: tierLoading } = useQuery({
    queryKey: ["subscription-tier", tierId],
    enabled: !!tierId,
    staleTime: 60 * 60 * 1000, // 1h — tiers change very rarely
    gcTime: 2 * 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_tiers")
        .select("*")
        .eq("id", tierId!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  return {
    tier: data ?? null,
    tierId: data?.id ?? null,
    tierSlug: data?.slug ?? null,
    tierName: data?.name ?? null,
    isLoading: profileLoading || (!!tierId && tierLoading),
    hasTier: !!data,
  };
}
